import React from 'react';

/**
 * Íconos de línea (estilo Lucide) para la sección "Contacto básico".
 * Se usan SVG locales porque el set `pcom-icon` no incluye varios de los
 * glifos del diseño (maletín, RSS, tarjeta de identificación, etc.) y el
 * trazo fino encaja mejor con el mockup.
 */
type IconProps = { size?: number; className?: string };

const Svg = ({ size = 16, className, children }: IconProps & { children: React.ReactNode }) => (
    <svg
        className={className}
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={1.8}
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
    >
        {children}
    </svg>
);

/** Tarjeta de identificación (ID). */
const IdGlyph = (p: IconProps) => (
    <Svg {...p}>
        <rect x="2" y="5" width="20" height="14" rx="2" />
        <circle cx="8" cy="10.5" r="1.9" />
        <path d="M4.6 16.2c.5-1.4 2-2.1 3.4-2.1s2.9.7 3.4 2.1" />
        <line x1="14.5" y1="10" x2="18.5" y2="10" />
        <line x1="14.5" y1="14" x2="18.5" y2="14" />
    </Svg>
);

/** Persona (Nombre). */
const UserGlyph = (p: IconProps) => (
    <Svg {...p}>
        <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2" />
        <circle cx="12" cy="7" r="4" />
    </Svg>
);

/** Persona dentro de un marco (Apellido). */
const UserSquareGlyph = (p: IconProps) => (
    <Svg {...p}>
        <rect x="3" y="3" width="18" height="18" rx="2" />
        <circle cx="12" cy="10" r="3" />
        <path d="M6.8 19c.6-2.1 2.7-3.1 5.2-3.1s4.6 1 5.2 3.1" />
    </Svg>
);

/** Sobre (Email). */
const MailGlyph = (p: IconProps) => (
    <Svg {...p}>
        <rect x="2" y="4.5" width="20" height="15" rx="2" />
        <path d="m2.5 7 9.5 6 9.5-6" />
    </Svg>
);

/** Teléfono (Tel contacto). */
const PhoneGlyph = (p: IconProps) => (
    <Svg {...p}>
        <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92z" />
    </Svg>
);

/** Burbuja de chat (WhatsApp). */
const ChatGlyph = (p: IconProps) => (
    <Svg {...p}>
        <path d="M7.9 20A9 9 0 1 0 4 16.1L2 22Z" />
    </Svg>
);

/** RSS / Feed. */
const RssGlyph = (p: IconProps) => (
    <Svg {...p}>
        <path d="M4 11a9 9 0 0 1 9 9" />
        <path d="M4 4a16 16 0 0 1 16 16" />
        <circle cx="5" cy="19" r="1" />
    </Svg>
);

/** Edificio (Tipo de broker). */
const BuildingGlyph = (p: IconProps) => (
    <Svg {...p}>
        <path d="M6 22V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v18Z" />
        <path d="M6 12H4a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h2" />
        <path d="M18 9h2a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2h-2" />
        <line x1="10" y1="6" x2="14" y2="6" />
        <line x1="10" y1="10" x2="14" y2="10" />
        <line x1="10" y1="14" x2="14" y2="14" />
        <line x1="10" y1="18" x2="14" y2="18" />
    </Svg>
);

/** Maletín (Gestor comercial). */
const BriefcaseGlyph = (p: IconProps) => (
    <Svg {...p}>
        <rect x="2" y="7" width="20" height="14" rx="2" />
        <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
        <line x1="2" y1="13" x2="22" y2="13" />
    </Svg>
);

/** Círculo con check (badge OTP validado). */
const CheckCircleGlyph = (p: IconProps) => (
    <Svg {...p}>
        <circle cx="12" cy="12" r="9" />
        <path d="m8.5 12 2.4 2.4 4.6-5" />
    </Svg>
);

/** Círculo con X (badge "sin validar"). */
const CloseCircleGlyph = (p: IconProps) => (
    <Svg {...p}>
        <circle cx="12" cy="12" r="9" />
        <path d="m9 9 6 6" />
        <path d="m15 9-6 6" />
    </Svg>
);

/** Rayo (Premium / Leads). */
const BoltGlyph = (p: IconProps) => (
    <Svg {...p}>
        <path d="M13 2 4 14h7l-1 8 9-12h-7z" />
    </Svg>
);

/** Infinito (Oportunidades). */
const InfinityGlyph = (p: IconProps) => (
    <Svg {...p}>
        <path d="M12 12c-2-2.8-4-4.2-6-4.2a4.2 4.2 0 1 0 0 8.4c2 0 4-1.4 6-4.2Zm0 0c2 2.8 4 4.2 6 4.2a4.2 4.2 0 0 0 0-8.4c-2 0-4 1.4-6 4.2Z" />
    </Svg>
);

/** Estrella (Destacados). */
const StarGlyph = (p: IconProps) => (
    <Svg {...p}>
        <path d="M12 3l2.6 5.3 5.9.9-4.2 4.1 1 5.8L12 16.9 6.7 19.2l1-5.8L3.5 9.2l5.9-.9z" />
    </Svg>
);

/** Corona (Prime). */
const CrownGlyph = (p: IconProps) => (
    <Svg {...p}>
        <path d="M3 7l4 4 5-7 5 7 4-4-2.2 11H5.2z" />
        <path d="M5 21h14" />
    </Svg>
);

/** Diamante (Elite). */
const GemGlyph = (p: IconProps) => (
    <Svg {...p}>
        <path d="M6 3h12l4 6-10 12L2 9z" />
        <path d="M2 9h20" />
        <path d="M9 3 7 9l5 12 5-12-2-6" />
    </Svg>
);

const ICONS = {
    id: IdGlyph,
    user: UserGlyph,
    'user-square': UserSquareGlyph,
    mail: MailGlyph,
    phone: PhoneGlyph,
    chat: ChatGlyph,
    rss: RssGlyph,
    building: BuildingGlyph,
    briefcase: BriefcaseGlyph,
    check: CheckCircleGlyph,
    close: CloseCircleGlyph,
    bolt: BoltGlyph,
    infinity: InfinityGlyph,
    star: StarGlyph,
    crown: CrownGlyph,
    gem: GemGlyph,
} as const;

export type ContactIconName = keyof typeof ICONS;

const ContactIcon = ({
    name,
    size,
    className,
}: {
    name: ContactIconName;
} & IconProps) => {
    const Glyph = ICONS[name];
    return <Glyph size={size} className={className} />;
};

export default ContactIcon;
